export const INFINITE_CONTACT_SEARCH_LABEL = 'Search name, email or phone';

export type InfiniteContactSearchFields = {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  phonePrefix?: string | null;
};

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Matches a free-text query against a traveller's name, email, or phone.
 * Phone matching ignores spaces and punctuation, and treats a local leading 0
 * as the same number stored without it.
 */
export function matchesInfiniteContactSearch(contact: InfiniteContactSearchFields, query: string): boolean {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return true;
  }

  const firstName = contact.firstName?.trim().toLowerCase() ?? '';
  const lastName = contact.lastName?.trim().toLowerCase() ?? '';
  const email = contact.email?.trim().toLowerCase() ?? '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  const phoneDisplay = [contact.phonePrefix, contact.phone].filter(Boolean).join(' ').toLowerCase();

  if (
    email.includes(normalizedQuery) ||
    firstName.includes(normalizedQuery) ||
    lastName.includes(normalizedQuery) ||
    fullName.includes(normalizedQuery) ||
    phoneDisplay.includes(normalizedQuery)
  ) {
    return true;
  }

  const queryTokens = normalizedQuery.split(/\s+/).filter(Boolean);
  if (queryTokens.length > 1) {
    const nameAndEmail = `${fullName} ${email}`;
    if (queryTokens.every((token) => nameAndEmail.includes(token))) {
      return true;
    }
  }

  const queryDigits = digitsOnly(normalizedQuery);
  if (!queryDigits) {
    return false;
  }

  const phoneDigits = digitsOnly(contact.phone ?? '');
  const prefixDigits = digitsOnly(contact.phonePrefix ?? '');
  const combinedDigits = `${prefixDigits}${phoneDigits}`;
  const localDigits = phoneDigits ? `0${phoneDigits.replace(/^0/, '')}` : '';

  return phoneDigits.includes(queryDigits) || combinedDigits.includes(queryDigits) || localDigits.includes(queryDigits);
}
